// Sentry initialization should be imported first!
import './index.css'
// import 'design-system/styles' // TODO: Fix Tailwind v4 conflict
import Cookies from 'js-cookie'
import {
  ATTRIBUTION_STORAGE_KEY,
  captureAttribution,
} from '@/utils/attribution'
import { StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider as ReduxProvider } from 'react-redux'
import { RouterProvider } from 'react-router'
import { MotionGlobalConfig } from 'framer-motion'
import store from '@/state/store'
import { sentryCreateBrowserRouter } from './instrument'
import { routes } from './routes'

// First, import performance utilities
import { initializePerformanceOptimizations } from './utils/performance'

// Initialize performance optimizations immediately to improve page loading
initializePerformanceOptimizations()

// Preload Square SDK for faster payment initialization

const router = sentryCreateBrowserRouter(routes as any)

const isLowPerformanceDevice = () => {
  // Check if device has less than 4 logical CPU cores
  const isIphone = /iPhone/i.test(navigator.userAgent)
  if (isIphone) {
    return false
  }

  const cpuCores = navigator.hardwareConcurrency || 1
  return cpuCores <= 4
}
MotionGlobalConfig.skipAnimations = isLowPerformanceDevice()

if (window.location.hostname === 'localhost') {
  //  document.domain = 'localhost'
}

// old site - when we are ready we will force people over to the new stampede site
if (window.location.hostname === 'product.blackbx.io') {
  window.location.href = `https://product.stampede.ai${window.location.pathname}${window.location.search}`
}

if (window.location.hostname.startsWith('connect')) {
  window.location.href = `https://${window.location.hostname.replace(
    'connect',
    'product'
  )}/${window.location.pathname}`
}

if (
  Cookies.get('theme') === 'dark'
    ? true
    : window.matchMedia('(prefers-color-scheme: dark)').matches
) {
  document.documentElement.classList.add('dark')
}

if ('standalone' in window.navigator && window.navigator.standalone) {
  // Check if running as PWA on iOS
  /*
  document.documentElement.style.overflow = 'hidden'
  document.body.style.overflow = 'hidden'


  document.body.addEventListener(
    'touchmove',
    function (e) {
      e.preventDefault()
    },
    { passive: false }
  )
    */
  document.body.style.position = 'fixed'
  document.body.style.top = `-${window.scrollY}px`
  document.body.style.left = '0'
  document.body.style.right = '0'

  window.addEventListener('scroll', (e: any) => {
    // Check if any input, textarea, or contenteditable element is focused
    const activeElement = document.activeElement
    const isKeyboardOpen =
      activeElement instanceof HTMLInputElement ||
      activeElement instanceof HTMLTextAreaElement ||
      activeElement?.hasAttribute('contenteditable')

    // Only prevent scroll and reset position if keyboard is not open
    if (!isKeyboardOpen) {
      e.preventDefault()
      window.scrollTo(0, 0)
    }
  })
}

// Global recovery for stale code-split chunks after a deploy. Any dynamic
// import() that fails outside the route error boundary (e.g. a click-loaded
// modal, a saga, or a non-route lazy import) rejects with a "Failed to fetch
// dynamically imported module" error. Reload once to pick up the new
// index.html + hashed chunks. Hover-prefetch failures are caught at the source
// (see preloadRouteByHref in routes.tsx) so they never reach here, and the
// 10s guard (shared key with the route boundary) prevents a reload loop.
function isStaleChunkError(reason: unknown): boolean {
  const msg = String(
    (reason as { message?: string } | null)?.message ?? reason ?? ''
  )
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('ChunkLoad') ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk') ||
    msg.includes("'text/html' is not a valid JavaScript MIME")
  )
}

window.addEventListener('unhandledrejection', (event) => {
  if (!isStaleChunkError(event.reason)) return
  event.preventDefault()
  const last = Number(sessionStorage.getItem('chunk-error-reload-at') ?? 0)
  if (Date.now() - last > 10_000) {
    sessionStorage.setItem('chunk-error-reload-at', String(Date.now()))
    window.location.reload()
  }
})

// First-touch signup attribution: capture UTMs / click-ids / referrer /
// landing_page into localStorage on the true landing URL, before render and
// before any route runs, so an unauthenticated visitor's campaign survives the
// axios 401 bounce to /oauth/logout and rides through to accounts' /register
// (see src/pages/oauth/logout.tsx). Write-once + failure-tolerant.
captureAttribution(ATTRIBUTION_STORAGE_KEY)

const createStore = store()

// Function to clean localStorage
function cleanLocalStorage() {
  try {
    const itemsToRemove: string[] = []

    // Iterate through all localStorage items
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (
        key &&
        !key.includes('user-credentials') &&
        !key.includes('user-config') &&
        !key.startsWith('connect:') &&
        // Signup attribution MUST survive the sweep. It's captured above on the
        // landing URL and read back at /oauth/logout to forward the campaign to
        // accounts — this cleaner runs one tick after capture, so without this
        // exemption the UTMs were wiped before the handover ever happened.
        key !== ATTRIBUTION_STORAGE_KEY
      ) {
        itemsToRemove.push(key)
      }
    }

    // Remove collected items
    itemsToRemove.forEach((key: any) => {
      localStorage.removeItem(key)
      console.log(`Cleaned up localStorage item: ${key}`)
    })
  } catch (error) {
    console.error('Error cleaning localStorage:', error)
  }
}

// Listen to storage events across tabs
window.addEventListener('storage', (event: any) => {
  console.log('Storage event:', event)
  if (event.key === 'syncData') {
    console.log('Storage event: Data synchronized across tabs:', event.newValue)
  }
})

// Set up periodic cleanup (runs every 5 minutes)
const CLEANUP_INTERVAL = 5 * 60 * 1000 // 5 minutes in milliseconds
setInterval(cleanLocalStorage, CLEANUP_INTERVAL)

// Run initial cleanup
cleanLocalStorage()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ReduxProvider store={createStore}>
      <RouterProvider router={router} />
    </ReduxProvider>
  </StrictMode>
)
